import React from 'react';
import { BarChart2, TrendingUp, TrendingDown, AlertTriangle, Info } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для визуализации анализа риска банкротства
 */
const RiskAnalysisChart = ({ analysisData, calculationModel }) => {
  if (!analysisData) return null;

  // Преобразуем данные для диаграммы
  let chartData = [];
  const getRiskColor = (value, threshold, isPositive = false) => {
    // Для коэффициентов, где большее значение - лучше (например, коэффициент текущей ликвидности)
    if (isPositive) {
      if (value >= threshold * 1.25) return '#10B981'; // Зеленый (хорошо)
      if (value >= threshold) return '#FBBF24'; // Желтый (удовлетворительно)
      return '#EF4444'; // Красный (плохо)
    } 
    // Для коэффициентов, где меньшее значение - лучше (например, коэффициент долговой нагрузки)
    else {
      if (value <= threshold * 0.75) return '#10B981'; // Зеленый (хорошо)
      if (value <= threshold) return '#FBBF24'; // Желтый (удовлетворительно)
      return '#EF4444'; // Красный (плохо)
    }
  };

  // Обработка данных различных моделей
  if (calculationModel === 'altman-z') {
    chartData = [
      { name: 'Z-счет', value: analysisData.score, threshold: analysisData.thresholds.safe, isPositiveIndicator: true },
      { name: 'X1 (Рабочий капитал / Активы)', value: analysisData.components.x1, threshold: 0.3, isPositiveIndicator: true },
      { name: 'X2 (Нераспред. прибыль / Активы)', value: analysisData.components.x2, threshold: 0.1, isPositiveIndicator: true },
      { name: 'X3 (EBIT / Активы)', value: analysisData.components.x3, threshold: 0.15, isPositiveIndicator: true },
      { name: 'X4 (Капитал / Обязательства)', value: analysisData.components.x4, threshold: 1.0, isPositiveIndicator: true },
      { name: 'X5 (Выручка / Активы)', value: analysisData.components.x5, threshold: 1.0, isPositiveIndicator: true }
    ];
  } else if (calculationModel === 'fulmer') {
    chartData = [
      { name: 'H-показатель', value: analysisData.score, threshold: 0, isPositiveIndicator: true },
      // Добавляем компоненты модели Фулмера, если они доступны
      ...(analysisData.components ? Object.entries(analysisData.components).map(([key, value]) => {
        // Определяем пороговое значение в зависимости от коэффициента
        let threshold = 0;
        switch(key) {
          case 'v1': threshold = 0.1; break; // Retained Earnings / Total Assets
          case 'v2': threshold = 0.5; break; // Sales / Total Assets
          case 'v3': threshold = 0.02; break; // EBT / Equity
          case 'v4': threshold = 0.01; break; // Cash Flow / Total Debt
          case 'v5': threshold = 0.3; break; // Debt / Total Assets
          default: threshold = 0;
        }
        return { 
          name: key, 
          value, 
          threshold,
          isPositiveIndicator: key !== 'v5' // v5 - это долговой коэффициент, для него меньше - лучше
        };
      }) : [])
    ];
  } else if (calculationModel === 'springate' || calculationModel === 'taffler') {
    // Структура для моделей с меньшим количеством коэффициентов
    chartData = [
      { 
        name: calculationModel === 'springate' ? 'Z-показатель Спрингейта' : 'Z-показатель Таффлера', 
        value: analysisData.score, 
        threshold: analysisData.thresholds.safe,
        isPositiveIndicator: true 
      },
      ...(analysisData.components ? Object.entries(analysisData.components).map(([key, value]) => {
        return { 
          name: key, 
          value, 
          threshold: 0.1, // Примерное пороговое значение
          isPositiveIndicator: true 
        };
      }) : [])
    ];
  } else if (calculationModel === 'comprehensive') {
    // Для комплексной оценки показываем результаты всех включенных моделей
    chartData = [
      { name: 'Комплексная оценка', value: analysisData.score, threshold: 0.5, isPositiveIndicator: true },
      { name: 'Модель Альтмана', value: analysisData.models.altman, threshold: 2.7, isPositiveIndicator: true },
      { name: 'Модель Фулмера', value: analysisData.models.fulmer, threshold: 0, isPositiveIndicator: true },
      { name: 'Модель Спрингейта', value: analysisData.models.springate, threshold: 0.862, isPositiveIndicator: true },
      { name: 'Модель Таффлера', value: analysisData.models.taffler, threshold: 0.3, isPositiveIndicator: true }
    ];
  } else {
    // Для других моделей обрабатываем общую структуру
    chartData = [
      { name: 'Итоговый показатель', value: analysisData.score, threshold: analysisData.thresholds.safe || 0, isPositiveIndicator: true },
      ...(analysisData.components ? Object.entries(analysisData.components).map(([key, value]) => {
        return { 
          name: key, 
          value, 
          threshold: 0, // Значение по умолчанию
          isPositiveIndicator: true 
        };
      }) : [])
    ];
  }
  
  // Информация о динамике показателей
  const trend = analysisData.trend || {
    overall: 'stable',
    liquidity: 'improving',
    profitability: 'stable',
    debt: 'worsening'
  };

  // Функция для получения иконки тренда
  const getTrendIcon = (trendType) => {
    switch(trendType) {
      case 'improving':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'worsening':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      case 'stable':
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  // Функция для получения текста тренда
  const getTrendText = (trendType) => {
    switch(trendType) {
      case 'improving':
        return 'Улучшение';
      case 'worsening':
        return 'Ухудшение';
      case 'stable':
      default:
        return 'Стабильность';
    }
  };

  // Определяем высоту графика в зависимости от количества элементов
  const chartHeight = Math.max(400, chartData.length * 60);
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const value = data.value;
      const threshold = data.threshold;
      const isPositiveIndicator = data.isPositiveIndicator;
      
      let statusText;
      let statusColor;
      
      if (isPositiveIndicator) {
        if (value >= threshold * 1.25) {
          statusText = "Хороший показатель";
          statusColor = "text-green-500";
        } else if (value >= threshold) {
          statusText = "Удовлетворительный показатель";
          statusColor = "text-yellow-500";
        } else {
          statusText = "Плохой показатель";
          statusColor = "text-red-500";
        }
      } else {
        if (value <= threshold * 0.75) {
          statusText = "Хороший показатель";
          statusColor = "text-green-500";
        } else if (value <= threshold) {
          statusText = "Удовлетворительный показатель";
          statusColor = "text-yellow-500";
        } else {
          statusText = "Плохой показатель";
          statusColor = "text-red-500";
        }
      }
      
      return (
        <div className="custom-tooltip bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
          <p className="font-medium">{label}</p>
          <p>Значение: <span className="font-semibold">{formatNumber(value, 3)}</span></p>
          <p>Пороговое значение: {formatNumber(threshold, 2)}</p>
          <p className={statusColor + " font-medium"}>{statusText}</p>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart2 className="w-5 h-5 mr-2 text-orange-500" />
        Анализ финансовых коэффициентов
      </h3>

      {/* Диаграмма финансовых коэффициентов */}
      <div className="mt-4">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" 
              tickFormatter={(value) => formatNumber(value, 2)} 
            />
            <YAxis 
              type="category" 
              dataKey="name"
              width={150}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" fill="#8884d8">
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getRiskColor(entry.value, entry.threshold, entry.isPositiveIndicator)} 
                />
              ))}
            </Bar>
            {chartData.map((entry, index) => (
              <ReferenceLine
                key={`ref-${index}`}
                y={entry.name}
                x={entry.threshold}
                stroke="#FFB000"
                strokeWidth={2}
                strokeDasharray="3 3"
                isFront={true}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Интерпретация результатов */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3">
          Интерпретация коэффициентов
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Динамика основных показателей */}
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-sm text-gray-600 dark:text-gray-400">Общая финансовая устойчивость:</span>
              <div className="flex items-center">
                {getTrendIcon(trend.overall)}
                <span className="ml-1 text-sm font-medium">{getTrendText(trend.overall)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-sm text-gray-600 dark:text-gray-400">Ликвидность:</span>
              <div className="flex items-center">
                {getTrendIcon(trend.liquidity)}
                <span className="ml-1 text-sm font-medium">{getTrendText(trend.liquidity)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-sm text-gray-600 dark:text-gray-400">Рентабельность:</span>
              <div className="flex items-center">
                {getTrendIcon(trend.profitability)}
                <span className="ml-1 text-sm font-medium">{getTrendText(trend.profitability)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Долговая нагрузка:</span>
              <div className="flex items-center">
                {getTrendIcon(trend.debt)}
                <span className="ml-1 text-sm font-medium">{getTrendText(trend.debt)}</span>
              </div>
            </div>
          </div>

          {/* Обобщенный анализ */}
          <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
            <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-1 text-orange-500" />
              Наибольшие риски
            </h5>
            <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 space-y-1">
              {analysisData.weakPoints && analysisData.weakPoints.length > 0 
                ? analysisData.weakPoints.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))
                : <li>Данные о критических показателях отсутствуют</li>
              }
            </ul>
          </div>
        </div>
      </div>

      {/* Пояснение к диаграмме */}
      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        <p>
          <span className="inline-block w-3 h-3 bg-green-500 mr-1"></span> Зеленый - хороший показатель
          <span className="inline-block w-3 h-3 bg-yellow-400 mr-1 ml-3"></span> Желтый - удовлетворительный показатель
          <span className="inline-block w-3 h-3 bg-red-500 mr-1 ml-3"></span> Красный - критический показатель
          <span className="inline-block w-3 h-0.5 bg-yellow-500 mr-1 ml-3 border-b border-t border-yellow-500 border-dashed"></span> Пунктир - пороговое значение
        </p>
      </div>
    </div>
  );
};

export default RiskAnalysisChart;